// Import dependencies
import { AutomatorLogItem } from '../_base';
import { unsafeCSS, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { sprintf } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logItemTrigger.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-item-trigger' )
export class AutomatorLogTrigger extends AutomatorLogItem {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * Returns the HTML of the section that shows the number of runs
	 * This will return HTML only if there were more than 1 runs.
	 *
	 * @return {TemplateResult} The number of runs
	 */
	get _templateRuns() {
		// Check if there if there is more than one, otherwise don't return anything
		if ( this.runs.length <= 1 ) {
			return html``;
		}

		return html`
			<div class="item__detail">
				<uo-icon class="item__detail-icon" id="hashtag"></uo-icon>

				${ sprintf( strings.timesNumber, this.runs.length ) }
			</div>
		`;
	}

	/**
	 * Creates the object passed to the sidebar
	 *
	 * @return {Object} The sidebar content
	 */
	get sidebarContent() {
		return {
			title: strings.sidebarTitle,
			summary: this.sidebarSummary,
			runs: this.sidebarRuns
		}
	}

	/**
	 * Creates the summary for the sidebar
	 *
	 * @return {Object} The sidebar summary
	 */
	get sidebarSummary() {
		// Summary items
		const items = [];

		// Add status
		items.push( {
			label: strings.summary.status,
			value: html`
				<uap-log-status status-id="${ this.statusID }"></uap-log-status>
			`
		} );

		// Add start and completion date
		// If it's a range, add "Start date" and "End date". If there is no end date, then just add "Date"
		const dateIsRange = this.startDate !== this.endDate;

		items.push( {
			label: dateIsRange ? strings.summary.startDate : strings.summary.date,
			value: this.startDate
		} );

		if ( dateIsRange ) {
			// Add "End date"
			items.push( {
				label: strings.summary.endDate,
				value: this.endDate
			} );
		}

		// Add the runs, but only if there is more than one
		if ( this.runs.length > 1 ) {
			items.push( {
				label: strings.summary.runs,
				value: this.runs.length
			} );
		}

		return {
			title: html`
				<uap-log-integration-icon integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
				<uap-log-sentence .sentence="${ this.titleHTML }"></uap-log-sentence>
			`,
			items: items
		}
	}

	/**
	 * Creates the array for the sidebar to render the trigger runs
	 *
	 * @return {Array} The array of runs
	 */
	get sidebarRuns() {
		const runs = [];

		// Get the number of runs
		const numberOfRuns = this.runs.length;

		if ( numberOfRuns === 0 ) {
			return;
		}

		this.runs.forEach( ( run, index ) => {
			// Create a summary of each run
			const runSummary = [];

			// Add the run status
			runSummary.push( {
				label: strings.summary.status,
				value: html`
					<uap-log-status status-id="${ run.status_id }"></uap-log-status>
				`
			} );

			// Add the date
			runSummary.push( {
				label: strings.summary.date,
				value: run.date
			} );

			runs.push( {
				title: html`
					<uap-log-status status-id="${ run.status_id }" hide-label></uap-log-status>
					${ sprintf( strings.runNumber, `${ ( index + 1 ) }/${ numberOfRuns }` ) }
				`,
				summary: runSummary,
				properties: [ ...this.fieldsForSidebar, ...run.properties ]
			} );
		} );

		return runs;
	}

	/**
	 * The message shown when the action data is missing
	 *
	 * @return {String} The message
	 */
	get dataMissingErrorMessage() {
		return strings.missingItem;
	}
}
